import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IShopifyCustomer } from '@app/_shared/_models';
import { CustomerService } from '../../_services/customer.service';
import { NgxSpinner, NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { GoogleAnalyticsService } from '../../_services/google-analytics.service';
import { MatDialogTitle, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    imports: [MatDialogTitle, MatIcon, MatDialogClose, FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatCheckbox, NgxSpinnerComponent, MatError, AsyncPipe]
})
export class RegisterComponent implements OnInit {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly spinnerService = inject(NgxSpinnerService);
  private readonly customerService = inject(CustomerService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  public spinner$!: Observable<NgxSpinner>;
  public firstName = new UntypedFormControl('', [Validators.required]);
  public lastName = new UntypedFormControl('', [Validators.required]);
  public email = new UntypedFormControl('', [Validators.required, Validators.email]);
  public password = new UntypedFormControl('', [Validators.required, Validators.minLength(8)]);
  public acceptsMarketing = new UntypedFormControl(true, [Validators.required]);
  public registerForm!: UntypedFormGroup;
  public errorMessage = '';
  public successMessage = 'An email has been sent to verify your account.';
  public success = false;

  constructor() {
    this.registerForm = this.formBuilder.group({
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      password: this.password,
      acceptsMarketing: this.acceptsMarketing
    });
  }

  ngOnInit(): void {
    this.spinner$ = this.spinnerService.spinnerObservable;
  }

  public async submit(): Promise<void> {
    this.success = false;
    this.errorMessage = this.getErrorMessage();

    if (!this.errorMessage) {
      const customer: IShopifyCustomer = {
        acceptsMarketing: this.acceptsMarketing.value,
        email: this.email.value,
        firstName: this.firstName.value,
        lastName: this.lastName.value,
        password: this.password.value
      }

      await this.spinnerService.show();
      this.googleAnalyticsService.eventEmitter('create_account', 'Register', 'Attempted to Create an Account', 'Account Creation', 1);
      const error = await this.customerService.registerCustomer(customer);
      await this.spinnerService.hide();
      if (error) {
        this.errorMessage = error;
      } else {
        this.success = true;
      }
    }
  }

  public getErrorMessage(): string {
    if (this.firstName.hasError('required')) {
      return 'Please enter your first name';
    } else if (this.lastName.hasError('required')) {
      return 'Please enter your last name';
    } else if (this.email.hasError('required')) {
      return 'Please enter your email';
    } else if (this.password.hasError('required')) {
      return 'Please enter a password';
    } else if (this.password.hasError('minlength')) {
      return 'Password must be at least 8 characters long'
    }

    return this.email.hasError('email') ? 'Not a valid email' : '';
  }
}
